import { IonIcon } from '@ionic/react';
import clsx from 'clsx';
import { IonicButton } from 'ionic-ui';
import {
  alertCircleOutline,
  checkmark,
  checkmarkDone,
  timerOutline,
} from 'ionicons/icons';
import { SendHorizonal } from 'lucide-react';
import { Message, QueuedMessage } from '../../../types/Messaging/Message';
import { transformDateToTime } from '../../../utils/transformDate';
import { Props } from './ChatMessageArea';
import { memo, useMemo } from 'react';

///=============== MAIN MESSAGE CARD
export const MessageCard = memo(
  ({
    userRoleId,
    onResend,
    ...msg
  }: (Message | QueuedMessage) & {
    userRoleId: number;
    onResend?: Props['onResend'];
  }) => {
    const isMine = msg.sender_role_id === userRoleId;

    const otherReaders = useMemo(() => {
      if (!('reads' in msg)) return [];
      // "Other readers" = anyone who read it that isn't me (match on role_id,
      // not the read-receipt row id).
      return msg.reads.filter((r) => r.role_id !== userRoleId);
    }, [msg, userRoleId]);

    const messageWrapperStyle = clsx(
      ' w-fit max-w-[80%] sm:max-w-[60%] space-y-1 my-2',
      isMine ? 'ml-auto' : 'mr-auto'
    );

    const messageWrapper = clsx(
      'py-2 px-3 w-fit max-w-full rounded-lg font-medium',
      isMine
        ? 'rounded-br-none bg-[#00c5e5] text-white ml-auto'
        : 'rounded-bl-none bg-gray-100 text-text-dark-high dark:bg-gray-700 dark:text-text-light-high mr-auto'
    );

    if ('isError' in msg && msg.isError) {
      return (
        <div className={`${messageWrapperStyle}`}>
          <div className="flex max-w-full items-start gap-0">
            <div className={`${messageWrapper} flex-1 opacity-60`}>
              <p className="max-w-full">{msg.body}</p>
            </div>
            <IonicButton
              className="py-3 px-2 h-fit min-w-fit"
              variant="ghost"
              onClick={() => {
                if (msg.id) {
                  return onResend?.(msg.id);
                }
              }}
            >
              <SendHorizonal className="size-4" />
            </IonicButton>
          </div>
          <small className="w-fit low-emphasis-text flex items-center gap-0.5">
            <IonIcon icon={alertCircleOutline} />
            <span>Failed</span>
          </small>
        </div>
      );
    }

    return (
      <div className={messageWrapperStyle}>
        <div className={messageWrapper}>
          <p className="max-w-full">{msg.body}</p>
        </div>
        {!('isSending' in msg) && (
          <small className="w-fit low-emphasis-text flex items-center gap-0.5 ">
            {msg.sender_role_id == userRoleId && (
              <>
                {otherReaders.length > 0 ? (
                  <IonIcon
                    icon={checkmarkDone}
                    className="text-primary size-3"
                  />
                ) : (
                  <IonIcon icon={checkmark} className="size-3" />
                )}
              </>
            )}

            <span>{transformDateToTime(msg.created_at)}</span>
          </small>
        )}
        {'isSending' in msg && msg.isSending && (
          <small className="w-fit low-emphasis-text flex items-center gap-0.5">
            <IonIcon icon={timerOutline} />
            <span>Sending</span>
          </small>
        )}
      </div>
    );
  }
);

export const MessageCardSkeleton = memo(({ count }: { count: number }) => {
  const messageWrapperStyle = clsx(
    'p-2 rounded-md text-gray-100 w-fit max-w-[80%] sm:max-w-[60%] mb-10 bg-gray-100 animate-pulse',
    count % 2 == 0 ? 'rounded-bl-none mr-auto' : 'rounded-br-none ml-auto'
  );

  return (
    <div className={messageWrapperStyle}>
      <p>message</p>
    </div>
  );
});
